import { apiError } from "../../../../../../utils/formErrors";
import TableSkeleton from "../../../../../../component/TableSkeleton";
import React, { useState, useEffect } from "react";
import { Box, Stack, Table, TableHead, TableBody, TableRow, TableCell, Dialog, Select, MenuItem } from "@mui/material";
import MainBox from "../../../../container/MainBox";
import BtnPrimary from "../../../../../../component/PrimaryButton";
import BtnSecondary from "../../../../../../component/SecondaryButton";
import FormField from "../../../../../../component/Formfield";
import EmptyState from "../../../../../../component/EmptyState";
import { StatusChip, selectSx, headCellSx, bodyCellSx } from "../../../../../../component/listing";
import { FieldLabel, SwitchRow } from "../../../../../../component/wizard";
import { getStatutoryComponents, createStatutoryComponent, updateStatutoryComponent } from "../../../../../../services/payroll";
import { getActiveLegalUnitId } from "../../../../../../services/globalData";
import { useNotify } from "../../../../../../component/NotificationProvider";
import { tokens, displayFont } from "../../../../../../theme";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

// preset templates to pre-fill the form; users can also build a fully custom one
const PRESETS = {
  EPF: { component: "EPF", name: "Employees' Provident Fund (EPF)", calcBasis: "PERCENT", baseField: "BASIC", ceiling: 15000, ceilingMode: "CAP", employeeRate: 12, employerRate: 12 },
  ESI: { component: "ESI", name: "Employees' State Insurance (ESI)", calcBasis: "PERCENT", baseField: "GROSS", ceiling: 21000, ceilingMode: "ELIGIBILITY", employeeRate: 0.75, employerRate: 3.25 },
  PROFESSIONAL_TAX: { component: "PROFESSIONAL_TAX", name: "Professional Tax", calcBasis: "FIXED", amount: 200 },
  LABOURW_WELFARE_FUND: { component: "LABOURW_WELFARE_FUND", name: "Labour Welfare Fund", calcBasis: "FIXED", amount: 25 },
  CUSTOM: { component: "", name: "", calcBasis: "PERCENT", baseField: "BASIC", ceiling: 0, ceilingMode: "NONE", employeeRate: 0, employerRate: 0 },
};

const blank = { editing: false, name: "", component: "", calcBasis: "PERCENT", baseField: "BASIC", ceiling: 0, ceilingMode: "NONE", employeeRate: 0, employerRate: 0, amount: 0, isActive: true };

const summary = (c) => {
  if ((c.calcBasis || (c.amount ? "FIXED" : "PERCENT")) === "FIXED") return `${inr(c.amount)} / month`;
  const base = (c.baseField || "BASIC") === "GROSS" ? "Gross" : "Basic";
  const ceil = (c.ceiling || c.wageCeiling)
    ? ` · ${c.ceilingMode === "ELIGIBILITY" ? "if ≤ " : "cap "}${inr(c.ceiling || c.wageCeiling)}`
    : "";
  return `EE ${c.employeeRate || 0}% · ER ${c.employerRate || 0}% of ${base}${ceil}`;
};

const StatutoryComponents = () => {
  const notify = useNotify();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [dialog, setDialog] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await getStatutoryComponents(getActiveLegalUnitId());
      if (res?.success === false) throw new Error(res?.message || "Failed to load statutory components");
      setData((Array.isArray(res?.data) ? res.data : []).filter((c) => !c.isDeleted));
    } catch (e) {
      setError(true);
      notify.error(e?.message || "Failed to load statutory components");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, []);

  const openNew = () => setDialog({ ...blank });
  const openEdit = (c) => setDialog({
    editing: true, _id: c._id, name: c.name || c.component || "", component: c.component || "",
    calcBasis: c.calcBasis || (c.amount && !c.employeeRate ? "FIXED" : "PERCENT"),
    baseField: c.baseField || "BASIC", ceiling: c.ceiling || c.wageCeiling || 0, ceilingMode: c.ceilingMode || "NONE",
    employeeRate: c.employeeRate || 0, employerRate: c.employerRate || 0, amount: c.amount || 0, isActive: c.isActive !== false,
  });
  const applyPreset = (k) => { const p = PRESETS[k]; setDialog((d) => ({ ...blank, ...p, editing: d.editing, _id: d._id, isActive: d.isActive })); };
  const set = (f, v) => setDialog((d) => ({ ...d, [f]: v }));

  const save = async () => {
    if (!dialog.name.trim()) { notify.error("Name is required"); return; }
    setSaving(true);
    try {
      const code = dialog.component || dialog.name.trim().toUpperCase().replace(/[^A-Z0-9]+/g, "_").slice(0, 40);
      const payload = dialog.calcBasis === "FIXED"
        ? { name: dialog.name.trim(), component: code, calcBasis: "FIXED", amount: Number(dialog.amount) || 0, isActive: dialog.isActive }
        : {
            name: dialog.name.trim(), component: code, calcBasis: "PERCENT", baseField: dialog.baseField,
            employeeRate: Number(dialog.employeeRate) || 0, employerRate: Number(dialog.employerRate) || 0,
            ceiling: Number(dialog.ceiling) || 0, ceilingMode: dialog.ceilingMode, isActive: dialog.isActive,
          };
      const res = dialog.editing
        ? await updateStatutoryComponent(dialog._id, payload)
        : await createStatutoryComponent({ ...payload, legalUnit: getActiveLegalUnitId() });
      if (res?.success === false) throw apiError(res, "Could not save");
      notify.success(dialog.editing ? "Statutory updated" : "Statutory component added");
      setDialog(null);
      fetchData();
    } catch (e) {
      notify.error(e?.message || "Could not save statutory component");
    } finally {
      setSaving(false);
    }
  };

  const isFixed = dialog?.calcBasis === "FIXED";

  return (
    <MainBox sx={{ padding: "1.25rem" }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: "1.25rem" }}>
        <Box sx={{ fontFamily: displayFont, fontSize: "1.25rem", fontWeight: 600, color: tokens.text }}>Statutory Components</Box>
        <BtnPrimary onClick={openNew} sx={{ borderRadius: "8px" }}>+ Add Statutory Component</BtnPrimary>
      </Stack>

      {loading ? (
          /* The header and the column widths are known before the data
             is, so they render immediately and nothing shifts when the
             rows land. */
          <Table sx={{ width: "100%", tableLayout: "fixed" }}>
            <TableHead>
            <TableRow>
              <TableCell sx={{ ...headCellSx, width: "40%" }}>Component</TableCell>
              <TableCell sx={headCellSx}>Configuration</TableCell>
              <TableCell sx={headCellSx}>Status</TableCell>
            </TableRow>
          </TableHead>
            <TableBody><TableSkeleton columns={3} /></TableBody>
          </Table>
        ) : error ? (
        <EmptyState variant="error" title="Couldn't load statutory components" onRetry={fetchData} />
      ) : data.length === 0 ? (
        <EmptyState title="No statutory components" description="Add EPF, ESI, PT, LWF or a custom statutory rule — applied automatically during payroll runs." />
      ) : (
        <Table sx={{ width: "100%", tableLayout: "fixed" }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headCellSx, width: "40%" }}>Component</TableCell>
              <TableCell sx={headCellSx}>Configuration</TableCell>
              <TableCell sx={headCellSx}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((c) => (
              <TableRow key={c._id} hover onClick={() => openEdit(c)} sx={{ cursor: "pointer", "&:hover": { background: tokens.surfaceSubtle } }}>
                <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>{c.name || c.component}</TableCell>
                <TableCell sx={{ ...bodyCellSx, color: tokens.textMuted }}>{summary(c)}</TableCell>
                <TableCell sx={bodyCellSx}><StatusChip label={c.isActive ? "Active" : "Inactive"} tone={c.isActive ? "success" : "muted"} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={Boolean(dialog)} onClose={() => setDialog(null)} PaperProps={{ sx: { borderRadius: "12px", p: "1.5rem", width: "34rem", maxWidth: "94vw" } }}>
        {dialog && (
          <>
            <Box sx={{ fontSize: "1rem", fontWeight: 600, color: tokens.text, mb: "1.25rem" }}>{dialog.editing ? "Edit statutory component" : "Add statutory component"}</Box>

            {!dialog.editing && (
              <Box sx={{ mb: "1.25rem" }}>
                <FieldLabel>Start from a preset (optional)</FieldLabel>
                <Select value="" displayEmpty onChange={(e) => applyPreset(e.target.value)} size="small" sx={{ ...selectSx, width: "100%" }}>
                  <MenuItem value="" disabled>Choose a preset…</MenuItem>
                  <MenuItem value="EPF">EPF (12% / 12%, cap ₹15,000)</MenuItem>
                  <MenuItem value="ESI">ESI (0.75% / 3.25%, if gross ≤ ₹21,000)</MenuItem>
                  <MenuItem value="PROFESSIONAL_TAX">Professional Tax (₹200 fixed)</MenuItem>
                  <MenuItem value="LABOURW_WELFARE_FUND">Labour Welfare Fund (₹25 fixed)</MenuItem>
                  <MenuItem value="CUSTOM">Custom (blank)</MenuItem>
                </Select>
              </Box>
            )}

            <Box sx={{ mb: "1.25rem" }}>
              <FieldLabel required>Name</FieldLabel>
              <FormField type="text" value={dialog.name} onChange={(e) => set("name", e.target.value)} fullwidth placeholder="e.g. National Pension Scheme" />
            </Box>

            <Box sx={{ mb: "1.25rem", maxWidth: "16rem" }}>
              <FieldLabel>Calculation</FieldLabel>
              <Select value={dialog.calcBasis} onChange={(e) => set("calcBasis", e.target.value)} size="small" sx={{ ...selectSx, width: "100%" }}>
                <MenuItem value="PERCENT">Percentage of pay</MenuItem>
                <MenuItem value="FIXED">Fixed amount</MenuItem>
              </Select>
            </Box>

            {isFixed ? (
              <Box sx={{ maxWidth: "14rem" }}>
                <FieldLabel>Amount (₹ / month)</FieldLabel>
                <FormField type="number" value={dialog.amount} onChange={(e) => set("amount", e.target.value)} fullwidth />
              </Box>
            ) : (
              <>
                <Stack direction="row" spacing={1.5} sx={{ flexWrap: "wrap", mb: "1rem" }}>
                  <Box sx={{ flex: 1, minWidth: "7rem" }}><FieldLabel>Employee %</FieldLabel><FormField type="number" value={dialog.employeeRate} onChange={(e) => set("employeeRate", e.target.value)} fullwidth /></Box>
                  <Box sx={{ flex: 1, minWidth: "7rem" }}><FieldLabel>Employer %</FieldLabel><FormField type="number" value={dialog.employerRate} onChange={(e) => set("employerRate", e.target.value)} fullwidth /></Box>
                  <Box sx={{ flex: 1, minWidth: "8rem" }}>
                    <FieldLabel>Base</FieldLabel>
                    <Select value={dialog.baseField} onChange={(e) => set("baseField", e.target.value)} size="small" sx={{ ...selectSx, width: "100%" }}>
                      <MenuItem value="BASIC">Basic</MenuItem>
                      <MenuItem value="GROSS">Gross</MenuItem>
                    </Select>
                  </Box>
                </Stack>
                <Stack direction="row" spacing={1.5} sx={{ flexWrap: "wrap" }}>
                  <Box sx={{ flex: 1, minWidth: "10rem" }}>
                    <FieldLabel>Ceiling rule</FieldLabel>
                    <Select value={dialog.ceilingMode} onChange={(e) => set("ceilingMode", e.target.value)} size="small" sx={{ ...selectSx, width: "100%" }}>
                      <MenuItem value="NONE">No ceiling</MenuItem>
                      <MenuItem value="CAP">Cap the base at ceiling</MenuItem>
                      <MenuItem value="ELIGIBILITY">Apply only if pay ≤ ceiling</MenuItem>
                    </Select>
                  </Box>
                  {dialog.ceilingMode !== "NONE" && (
                    <Box sx={{ flex: 1, minWidth: "9rem" }}><FieldLabel>Ceiling (₹ / month)</FieldLabel><FormField type="number" value={dialog.ceiling} onChange={(e) => set("ceiling", e.target.value)} fullwidth /></Box>
                  )}
                </Stack>
              </>
            )}

            <Box sx={{ mt: "1rem" }}>
              <SwitchRow title="Active" description="Applied automatically in payroll runs." checked={dialog.isActive} onChange={(e, c) => set("isActive", c)} />
            </Box>

            <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: "1.5rem" }}>
              <BtnSecondary onClick={() => setDialog(null)} disabled={saving} sx={{ borderRadius: "8px" }}>Cancel</BtnSecondary>
              <BtnPrimary onClick={save} disabled={saving} sx={{ borderRadius: "8px" }}>{saving ? "Saving…" : dialog.editing ? "Save" : "Add"}</BtnPrimary>
            </Stack>
          </>
        )}
      </Dialog>
    </MainBox>
  );
};

export default StatutoryComponents;
